import ky, { HTTPError } from "ky";
import { API_BASE_AUTH } from "@/constants/api";
import { ROUTES } from "@/constants/routes";

/**
 * The identity the backend reports for the current session.
 *
 * There is deliberately no token here, and no endpoint that returns one. The
 * session cookie proves who you are to this service; the platform credential
 * stays server-side and is attached by the proxy. Anything reachable from
 * JavaScript is reachable from an XSS.
 */
export interface Session {
  email: string;
  name: string;
  expiresAt: string;
}

export interface SessionResponse {
  email: string;
  name: string;
  expires_at: string;
}

/**
 * Maps the backend's wire shape to ours. Split out so it can be tested without
 * a fetch implementation: jsdom provides none, and the alternative is mocking
 * the HTTP client, which tests the mock rather than this.
 */
export const toSession = (body: SessionResponse): Session => ({
  email: body.email,
  expiresAt: body.expires_at,
  name: body.name,
});

const authClient = ky.create({
  baseUrl: API_BASE_AUTH,
  credentials: "same-origin",
});

/**
 * Asks the backend who the current user is.
 *
 * Returns `null` when there is no session — a 401 here is a normal answer, not
 * an error. Any other failure throws, so a backend that is unwell is not
 * mistaken for a logged-out user: those need different treatment, one being an
 * error state and the other a trip to the login page.
 */
export const fetchSession = async (): Promise<Session | null> => {
  try {
    return toSession(await authClient.get("session").json<SessionResponse>());
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 401) {
      return null;
    }
    throw error;
  }
};

/**
 * Starts a login by handing the browser to the backend, which redirects to
 * Auth0.
 *
 * A full-page navigation, not a fetch: the endpoint answers 302, and fetch
 * would follow that redirect opaquely without ever moving the browser, so the
 * login would silently never happen.
 */
export const startLogin = (email?: string): void => {
  // login_hint prefills the address on Auth0's own form (OIDC Core 3.1.2.1),
  // so someone who typed it here is not asked for it again. A convenience
  // only: Auth0 still authenticates them, and a hint naming somebody else
  // changes nothing about who can sign in.
  const query = email ? `?login_hint=${encodeURIComponent(email)}` : "";
  window.location.assign(`${API_BASE_AUTH}login${query}`);
};

/**
 * Signs an external user in with a password.
 *
 * Returns `false` when the credentials are rejected — a 401 here is an ordinary
 * answer, not an error, exactly as in `fetchSession`. Any other failure throws,
 * so a backend that is unwell is never reported to the user as a bad password.
 *
 * The password goes to this service, not to Auth0. The backend mangles it,
 * performs the Auth0 password-realm grant server-to-server, and answers 204
 * with a session cookie; the browser never receives a token.
 *
 * There is no unit test for this, for the same reason `fetchSession` has none:
 * the branching needs a fetch implementation, jsdom provides none, and mocking
 * the HTTP client would assert the mock. Verified against the running service
 * with `Origin: http://localhost:8080` (the origin guard answers 403 without
 * it): an address with no external_users row and an address with one both
 * return 401 with an identical body, which is the indistinguishability this
 * function's contract depends on. The 204 success path is unverified here.
 */
export const login = async (
  email: string,
  password: string
): Promise<boolean> => {
  try {
    await authClient.post("login", { json: { email, password } });
    return true;
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 401) {
      return false;
    }
    throw error;
  }
};

/**
 * Sets an invited user's first password, spending the token their invite email
 * carried.
 *
 * Returns `null` when the password was accepted. A 400 is an ordinary answer
 * here — a link that expired or was already used, or a password the backend's
 * policy refuses — so its message is returned for display rather than thrown.
 * Anything else throws, so a backend that is unwell is never reported to the
 * user as a bad link.
 *
 * The message is read from `error.data`, not `error.response.json()`: ky
 * consumes the response body while populating `data`, after which the body
 * methods no longer work.
 *
 * No session is created by this call, by the backend's deliberate design — the
 * user returns to the login page and signs in with what they just chose, which
 * proves it works while they are still in front of the form.
 */
export const setPassword = async (
  token: string,
  password: string
): Promise<string | null> => {
  try {
    await authClient.post("set-password", { json: { token, password } });
    return null;
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 400) {
      const body = error.data as { error?: string } | undefined;
      // An empty string still means "rejected"; the caller supplies wording
      // when the backend sends none.
      return body?.error ?? "";
    }
    throw error;
  }
};

/**
 * Asks for a password reset link.
 *
 * Deliberately has no success/failure distinction to report. The endpoint
 * answers 204 for every address — found, unknown, or disabled — because the
 * page promises "If an account exists for that email, a password reset link is
 * on its way", and an answer that varied would leak which it was.
 *
 * A transport failure still throws: the user pressed a button and nothing
 * happened, and telling them it worked would be a lie.
 */
export const requestPasswordReset = async (email: string): Promise<void> => {
  await authClient.post("reset-request", { json: { email } });
};

/**
 * Leaves for the application after a successful sign-in.
 *
 * A full page load rather than a router transition: the browser now holds a
 * session cookie, but every in-memory store still describes a logged-out app,
 * and only a reload re-runs `fetchSession`. `logout` navigates this way for the
 * same reason, and the staff path gets it for free from Auth0's 302.
 *
 * Separated from `login` so the form can distinguish a rejected credential from
 * a successful one before leaving the page.
 */
export const landAfterLogin = (): void => {
  window.location.assign(ROUTES.CAMPAIGN_OVERVIEW);
};

/**
 * Ends the session server-side, then leaves for the login page.
 *
 * The navigation is a full page load rather than a router transition, so every
 * in-memory store goes with it: whoever uses this browser next does not inherit
 * the previous user's cached data. It runs only after the request succeeds — a
 * rejection leaves the user where they are, because a session that failed to
 * end is still a session.
 *
 * This deliberately ends only our own session. Auth0's and Okta's are not ours
 * to end, and ending Okta's would sign the user out of every Infillion
 * application. Signing back in therefore will not ask for a password: that is
 * what single sign-on means here, not a failure of this call.
 */
export const logout = async (): Promise<void> => {
  await authClient.post("logout");
  window.location.assign(ROUTES.LOGIN);
};
